import React, { useEffect, useState } from 'react';
import {
  listSessionQuestions, attachSessionQuestion, attachSessionQuestionSet,
  updateSessionQuestion, detachSessionQuestion,
} from '../../api/sessionQuestions';
import { listQuestions } from '../../api/questions';
import { listQuestionSets } from '../../api/questionSets';
import Modal from '../ui/Modal';
import QuestionFilters from '../questions/QuestionFilters';
import QuestionCard from '../questions/QuestionCard';

/**
 * QuestionsTab - center-tab view that lets the host attach, run, and score
 * questions during a live session. Listens for `question_changed` events
 * (pushed via Dashboard) through the optional `lastChangeToken` prop.
 */
export default function QuestionsTab({ sessionId, lastChangeToken }) {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const reload = async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await listSessionQuestions(sessionId);
      setItems(res.data || []);
    } catch (e) {
      console.warn('[QuestionsTab] list failed', e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [sessionId, lastChangeToken]);

  const onMarkAsked = async (sq) => {
    try {
      await updateSessionQuestion(sessionId, sq.questionId, { markAsked: true });
      reload();
    } catch (e) { console.warn(e?.message); }
  };
  const onMarkAnswered = async (sq) => {
    try {
      await updateSessionQuestion(sessionId, sq.questionId, { markAnswered: true });
      reload();
    } catch (e) { console.warn(e?.message); }
  };
  const onScore = async (sq, score) => {
    try {
      await updateSessionQuestion(sessionId, sq.questionId, { score });
      reload();
    } catch (e) { console.warn(e?.message); }
  };
  const onNotes = async (sq, hostNotes) => {
    try {
      await updateSessionQuestion(sessionId, sq.questionId, { hostNotes });
    } catch (e) { console.warn(e?.message); }
  };
  const onDetach = async (sq) => {
    if (!window.confirm('Remove this question from the session?')) return;
    try {
      await detachSessionQuestion(sessionId, sq.questionId);
      reload();
    } catch (e) { console.warn(e?.message); }
  };

  return (
    <div className="h-full overflow-y-auto p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Session Questions ({items.length})
        </h3>
        <button
          onClick={() => setPickerOpen(true)}
          className="px-2 py-1 text-[11px] font-semibold rounded bg-teal-600
                     hover:bg-teal-500 text-white"
        >
          + Attach
        </button>
      </div>

      {loading && items.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-6">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-500 text-center py-6">
          No questions attached yet.
        </div>
      ) : (
        items.map((sq) => (
          <SessionQuestionRow
            key={sq.id}
            sq={sq}
            onMarkAsked={() => onMarkAsked(sq)}
            onMarkAnswered={() => onMarkAnswered(sq)}
            onScore={(s) => onScore(sq, s)}
            onNotes={(n) => onNotes(sq, n)}
            onDetach={() => onDetach(sq)}
          />
        ))
      )}

      <Picker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        sessionId={sessionId}
        onAttached={reload}
      />
    </div>
  );
}

function SessionQuestionRow({ sq, onMarkAsked, onMarkAnswered, onScore, onNotes, onDetach }) {
  const [notes, setNotes] = useState(sq.hostNotes || '');
  const [score, setScore] = useState(sq.score ?? '');

  useEffect(() => { setNotes(sq.hostNotes || ''); }, [sq.hostNotes]);
  useEffect(() => { setScore(sq.score ?? ''); }, [sq.score]);

  return (
    <article className="rounded-lg border border-slate-800 bg-slate-900/60 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] text-slate-500">#{sq.position + 1}</p>
          <p className="text-sm font-semibold text-slate-100 truncate">
            {sq.questionTitle || sq.questionId}
          </p>
        </div>
        <button
          onClick={onDetach}
          className="text-[10px] text-slate-500 hover:text-red-400"
        >
          remove
        </button>
      </div>

      <div className="flex items-center gap-2 text-[10px]">
        <button
          disabled={!!sq.askedAt}
          onClick={onMarkAsked}
          className="px-2 py-1 rounded font-semibold border border-slate-700 disabled:opacity-50
                     bg-slate-800 hover:bg-slate-700 text-slate-200"
        >
          {sq.askedAt ? `Asked ${fmt(sq.askedAt)}` : 'Mark asked'}
        </button>
        <button
          disabled={!sq.askedAt || !!sq.answeredAt}
          onClick={onMarkAnswered}
          className="px-2 py-1 rounded font-semibold border border-slate-700 disabled:opacity-50
                     bg-slate-800 hover:bg-slate-700 text-slate-200"
        >
          {sq.answeredAt ? `Answered ${fmt(sq.answeredAt)}` : 'Mark answered'}
        </button>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-[10px] text-slate-500">Score</label>
        <input
          type="number" min={0} max={100}
          value={score}
          onChange={(e) => setScore(e.target.value)}
          onBlur={() => {
            const n = score === '' ? null : Math.max(0, Math.min(100, Number(score)));
            if (n !== sq.score) onScore(n);
          }}
          className="w-20 bg-slate-950 border border-slate-700 rounded text-xs text-slate-100 px-2 py-1"
        />
      </div>

      <textarea
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={() => { if (notes !== (sq.hostNotes || '')) onNotes(notes); }}
        placeholder="Per-question notes"
        className="w-full resize-none rounded bg-slate-950 border border-slate-800
                   text-slate-200 text-xs p-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
    </article>
  );
}

function fmt(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}

function Picker({ open, onClose, sessionId, onAttached }) {
  const [tab,     setTab]     = useState('individual'); // 'individual' | 'set'
  const [filters, setFilters] = useState({ search: '', category: '', difficulty: '', active: undefined });
  const [data,    setData]    = useState({ content: [] });
  const [sets,    setSets]    = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (tab === 'individual') {
      const t = setTimeout(async () => {
        setLoading(true);
        try {
          const params = { size: 30, page: 0 };
          if (filters.search)     params.search     = filters.search;
          if (filters.category)   params.category   = filters.category;
          if (filters.difficulty) params.difficulty = filters.difficulty;
          const res = await listQuestions(params);
          setData(res.data);
        } finally { setLoading(false); }
      }, 200);
      return () => clearTimeout(t);
    } else {
      (async () => {
        setLoading(true);
        try {
          const res = await listQuestionSets();
          setSets(res.data || []);
        } finally { setLoading(false); }
      })();
    }
  }, [open, tab, filters]);

  const attachOne = async (q) => {
    try { await attachSessionQuestion(sessionId, { questionId: q.id }); onAttached(); }
    catch (e) { console.warn(e?.message); }
  };
  const attachSet = async (s) => {
    try { await attachSessionQuestionSet(sessionId, s.id); onAttached(); onClose(); }
    catch (e) { console.warn(e?.message); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Attach question(s)" size="lg">
      <div className="flex items-center gap-2 mb-3">
        <TabBtn active={tab==='individual'} onClick={() => setTab('individual')}>Individual</TabBtn>
        <TabBtn active={tab==='set'}        onClick={() => setTab('set')}>From set</TabBtn>
      </div>

      {tab === 'individual' ? (
        <div className="space-y-3">
          <QuestionFilters value={filters} onChange={setFilters} />
          <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
            {loading ? (
              <p className="text-xs text-slate-500 text-center py-4">Loading…</p>
            ) : data.content.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-4">No matches.</p>
            ) : data.content.map((q) => (
              <QuestionCard
                key={q.id}
                question={q}
                action={(
                  <button
                    onClick={() => attachOne(q)}
                    className="px-2 py-1 text-[11px] rounded bg-teal-600 hover:bg-teal-500
                               text-white font-semibold"
                  >
                    Attach
                  </button>
                )}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {loading ? (
            <p className="text-xs text-slate-500 text-center py-4">Loading…</p>
          ) : sets.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-4">No question sets.</p>
          ) : sets.map((s) => (
            <div key={s.id} className="rounded border border-slate-200 p-3 flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{s.name}</p>
                <p className="text-xs text-slate-500">{s.items?.length || 0} questions</p>
              </div>
              <button
                onClick={() => attachSet(s)}
                className="px-2 py-1 text-[11px] rounded bg-teal-600 hover:bg-teal-500
                           text-white font-semibold"
              >
                Attach all
              </button>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-semibold rounded ${
        active ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
    >
      {children}
    </button>
  );
}
